import React, { useState, useEffect } from "react";
import { FaPlay, FaTimes, FaFilm } from "react-icons/fa";

const DEFAULT_VIDEOS = [
    {
        id: "video-1",
        title: "Industrial Facade & Warehouse Projection",
        description: "High-power 150W LED gobo projector displaying crisp corporate emblem across large industrial surface.",
        thumbnail: "/assets/lumigo/projector-1.jpg",
        videoUrl: "/assets/lumigo/video-demo-1.mp4",
        duration: "0:45",
        category: "Industrial",
        isPublished: true,
        isDemo: true,
    },
    {
        id: "video-2",
        title: "Retail Entrance & Facade Branding",
        description: "Demonstration of outdoor IP65 weatherproof gobo projector casting high-definition branding onto building exterior.",
        thumbnail: "/assets/lumigo/projector-4.jpg",
        videoUrl: "/assets/lumigo/video-demo-2.mp4",
        duration: "0:30",
        category: "Architectural",
        isPublished: true,
        isDemo: true,
    },
    {
        id: "video-3",
        title: "Multi-Logo Glass Gobo Rotation",
        description: "Precision 4-logo motorized rotating gobo demonstration with razor-sharp dichroic glass focus.",
        thumbnail: "/assets/lumigo/projector-14.jpg",
        videoUrl: "/assets/lumigo/video-demo-3.mp4",
        duration: "0:38",
        category: "Hardware",
        isPublished: true,
        isDemo: true,
    },
    {
        id: "video-4",
        title: "Active Workplace Safety Floor Sign",
        description: "High-visibility pedestrian crossing and forklift warning sign projected continuously on active warehouse floor.",
        thumbnail: "/assets/lumigo/projector-16.jpg",
        videoUrl: "/assets/lumigo/video-demo-4.mp4",
        duration: "0:25",
        category: "Safety",
        order: 4,
        isPublished: true,
        isDemo: true,
    },
];

const VideoDemonstration = ({ videoSection = {} }) => {
    const heading = videoSection.heading || "Projection Videos — See LUMIGO in Action";
    const subheading = videoSection.subheading || "Experience real-world performance, optical throw clarity, and gobo rotations across industrial and architectural installations.";
    const rawVideos = videoSection.videos && videoSection.videos.length > 0 ? videoSection.videos : DEFAULT_VIDEOS;
    const publishedVideos = rawVideos.filter(v => v.isPublished !== false);

    const [activeVideo, setActiveVideo] = useState(null);

    // Close modal on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setActiveVideo(null);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    if (publishedVideos.length === 0) return null;

    return (
        <section className="py-18 bg-[#F7F5EF] border-y border-[#E5E7EB]">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E3B629]/15 border border-[#E3B629]/30 text-[#263746] text-xs font-black uppercase tracking-widest mb-3">
                        <FaFilm className="text-[#E3B629]" />
                        <span>Real Hardware Demonstrations</span>
                    </div>
                    <h2 className="text-2xl sm:text-4xl font-extrabold text-[#263746] tracking-tight">
                        {heading}
                    </h2>
                    <p className="text-[#687482] text-xs sm:text-sm mt-3 leading-relaxed">
                        {subheading}
                    </p>
                </div>

                {/* Video Grid (4 Cards) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {publishedVideos.map((video) => (
                        <div
                            key={video.id}
                            onClick={() => setActiveVideo(video)}
                            className="group bg-white rounded-2xl overflow-hidden border border-[#E5E7EB] hover:border-[#E3B629] hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between"
                        >
                            {/* Auto-Playing Looping Video Card */}
                            <div className="relative aspect-video w-full bg-[#1A1A1A] overflow-hidden">
                                <video
                                    src={video.videoUrl}
                                    poster={video.thumbnail || "/assets/lumigo/projector-1.jpg"}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/20 group-hover:bg-black/30 transition-colors pointer-events-none" />

                                {/* Play / Expand Overlay */}
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="w-12 h-12 rounded-full bg-[#E3B629] text-[#263746] flex items-center justify-center text-sm shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                                        <FaPlay className="ml-0.5" />
                                    </div>
                                </div>

                                {/* Category Badge */}
                                <span className="absolute top-3 left-3 bg-[#263746]/90 backdrop-blur-xs text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-md border border-white/20">
                                    {video.category || "Commercial"}
                                </span>

                                {/* Live Auto-Play Tag */}
                                <span className="absolute bottom-3 left-3 bg-black/60 text-[#E3B629] text-[9px] font-bold px-2 py-0.5 rounded flex items-center gap-1 backdrop-blur-xs">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#E3B629] animate-pulse" />
                                    <span>AUTO PLAY</span>
                                </span>

                                {/* Duration Badge */}
                                {video.duration && (
                                    <span className="absolute bottom-3 right-3 bg-black/80 text-white text-[11px] font-mono px-2 py-0.5 rounded">
                                        {video.duration}
                                    </span>
                                )}

                                {/* Demonstration Badge if marked demo */}
                                {video.isDemo && (
                                    <span className="absolute top-3 right-3 bg-[#E3B629] text-[#263746] text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded shadow-xs">
                                        Demo
                                    </span>
                                )}
                            </div>

                            {/* Card Content */}
                            <div className="p-5 flex-1 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-sm font-bold text-[#263746] group-hover:text-[#E3B629] transition-colors leading-snug line-clamp-2 mb-2">
                                        {video.title}
                                    </h3>
                                    <p className="text-xs text-[#687482] line-clamp-2 leading-relaxed">
                                        {video.description}
                                    </p>
                                </div>

                                <div className="mt-4 pt-3 border-t border-[#E5E7EB] flex items-center justify-between text-xs font-bold text-[#E3B629] group-hover:translate-x-0.5 transition-transform">
                                    <span>Watch Demonstration</span>
                                    <FaPlay className="text-[10px]" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Video Modal Player */}
            {activeVideo && (
                <div
                    className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-fadeIn"
                    onClick={() => setActiveVideo(null)}
                >
                    <div
                        className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="bg-[#1B2430] px-5 py-3.5 flex items-center justify-between border-b border-white/10">
                            <div>
                                <span className="text-[#E3B629] text-[10px] font-bold uppercase tracking-wider block">
                                    {activeVideo.category || "Projection Demo"}
                                </span>
                                <h3 className="text-sm sm:text-base font-bold text-white truncate max-w-md">
                                    {activeVideo.title}
                                </h3>
                            </div>
                            <button
                                onClick={() => setActiveVideo(null)}
                                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                                aria-label="Close Video"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Video Element */}
                        <div className="relative aspect-video w-full bg-black">
                            <video
                                src={activeVideo.videoUrl}
                                poster={activeVideo.thumbnail}
                                controls
                                autoPlay
                                playsInline
                                className="w-full h-full object-contain"
                            >
                                Your browser does not support HTML5 video playback.
                            </video>
                        </div>

                        {/* Video Subtitle & Hardware Note */}
                        <div className="bg-[#141B22] p-4 text-xs text-gray-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-white/5">
                            <p className="line-clamp-2 max-w-2xl">{activeVideo.description}</p>
                            <span className="text-[11px] text-[#E3B629] font-bold whitespace-nowrap">
                                LUMIGO by Fourlifters
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default VideoDemonstration;
